import { useEffect, useRef, useState } from 'react';
import { Calendar, Shield } from 'lucide-react';
import { useAuth } from '../../auth';

export function LoginPage() {
  const { login, isLoading, authMode } = useAuth();
  const [autoLoginError, setAutoLoginError] = useState<string | null>(null);
  const autoTriggered = useRef(false);

  const handleLogin = async () => {
    try {
      setAutoLoginError(null);
      await login();
    } catch (error) {
      console.error('Anmeldung fehlgeschlagen:', error);
      setAutoLoginError(
        authMode === 'teams'
          ? 'Teams-SSO fehlgeschlagen. Bitte HR informieren — möglicherweise muss die Stundenliste-App neu zugelassen werden.'
          : 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.'
      );
    }
  };

  // Im Teams-Tab: Login automatisch beim Mount triggern (kein Klick nötig).
  useEffect(() => {
    if (authMode !== 'teams' || autoTriggered.current || isLoading) return;
    autoTriggered.current = true;
    void handleLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authMode]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 p-8 space-y-6">
          {/* Logo und Titel */}
          <div className="text-center space-y-3">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full">
              <Calendar className="w-8 h-8 text-gray-700 dark:text-gray-300" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Stundenliste</h1>
            <p className="text-gray-600">
              Arbeitszeit- &amp; Urlaubsverwaltung
            </p>
          </div>

          {/* Login Button */}
          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-6 py-4
                       bg-[#0078d4] text-white rounded-lg font-medium
                       hover:bg-[#106ebe] transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed
                       "
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Anmeldung läuft...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                  <path d="M0 0H10V10H0V0Z" fill="#F25022" />
                  <path d="M11 0H21V10H11V0Z" fill="#7FBA00" />
                  <path d="M0 11H10V21H0V11Z" fill="#00A4EF" />
                  <path d="M11 11H21V21H11V11Z" fill="#FFB900" />
                </svg>
                <span>Mit Microsoft anmelden</span>
              </>
            )}
          </button>

          {autoLoginError && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-900/40 p-3 text-sm text-red-700 dark:text-red-300">
              {autoLoginError}
            </div>
          )}

          {/* Info-Box */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-700 mb-1">Sichere Anmeldung</p>
                <p>
                  Ihre Daten werden sicher in der Datenbank gespeichert und sind nur für
                  Sie und die HR-Abteilung zugänglich.
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-2 h-2 bg-gray-900 dark:bg-gray-100 rounded-full" />
              <span>Arbeitszeiterfassung</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-2 h-2 bg-gray-900 dark:bg-gray-100 rounded-full" />
              <span>Urlaubsplanung</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-2 h-2 bg-gray-900 dark:bg-gray-100 rounded-full" />
              <span>Automatische Berechnung</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Stundenliste — Berechnung nach deutschem Arbeitsrecht (BUrlG)
        </p>
      </div>
    </div>
  );
}
